import React, { Suspense } from 'react';
import { Route, Redirect } from 'react-router-dom';
import { useSelector } from 'react-redux';
import usePermissionCache from '../../Hooks/usePermissionCache';
import useCompany, {
  COMPANY_MODELS,
  hasCompanyModel,
} from '../../Hooks/company';
import LoadingSpiner from '../LoadingSpiner';
import { ErrorFallback } from '../ErrorFallback/ErrorFallback';
import { ErrorBoundary } from 'react-error-boundary';

interface PermissionRouteProps {
  component: React.ComponentType<any>;
  path: string;
  exact?: boolean;
  permissions?: string[];
  /** When true, all listed permissions are required (AND). Default is OR. */
  requireAllPermissions?: boolean;
  roles?: number[];
  userTypes?: number[];
  /**
   * When true, require the company `dms` model even for admins.
   * Admins still skip permission checks; this is a company capability gate.
   * (Does not check use_shopper — that gates order/shopper UI only.)
   */
  requiresDms?: boolean;
  /**
   * Require a company model by title (e.g. COMPANY_MODELS.PRESCRIPTION).
   * Applies even to admins — company capability gate, not a permission check.
   */
  requiresModel?: string;
  fallback?: React.ReactNode;
  redirectTo?: string;
}

export const PermissionRoute: React.FC<PermissionRouteProps> = ({
  component: Component,
  path,
  exact = false,
  permissions = [],
  requireAllPermissions = false,
  roles = [],
  userTypes = [],
  requiresDms = false,
  requiresModel,
  fallback,
  redirectTo = '/unauthorized',
  ...rest
}) => {
  const isAuthenticated = !!localStorage.getItem("token");
  const { userInfo } = useSelector((state: any) => state.setReducer);
  const { company, loading: companyLoading } = useCompany();
  const {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    cachedPermissions,
    isLoading,
  } = usePermissionCache();

  const isAdmin = cachedPermissions?.isAdmin || false;
  const userType = cachedPermissions?.userType;

  // Company capability gates — apply to admins too (unlike permission checks).
  // Sidebar/URL access: dms model only (not use_shopper).
  const hasDmsAccess =
    !requiresDms || hasCompanyModel(company, COMPANY_MODELS.DMS);
  const hasRequiredModelAccess =
    !requiresModel || hasCompanyModel(company, requiresModel);
  const needsCompanyGate = requiresDms || !!requiresModel;

  // Check permissions and roles
  const hasAccess = React.useMemo(() => {
    // Admin has access to everything (permissions/roles), but not company gates
    if (isAdmin) return true;

    // Check allowed user types (if provided)
    if (userTypes.length > 0) {
      if (typeof userType !== 'number' || !userTypes.includes(userType)) {
        return false;
      }
    }

    // Check if user has required roles
    if (roles.length > 0 && !hasRole(roles)) {
      return false;
    }

    // Check if user has required permissions
    if (permissions.length > 0) {
      const hasRequiredPermissions = requireAllPermissions
        ? hasAllPermissions(permissions)
        : hasAnyPermission(permissions);
      if (!hasRequiredPermissions) {
        return false;
      }
    }

    return true;
  }, [
    isAdmin,
    roles,
    permissions,
    requireAllPermissions,
    userTypes,
    userType,
    hasRole,
    hasAnyPermission,
    hasAllPermissions,
  ]);

  const missingPermission = React.useMemo(() => {
    if (permissions.length === 0) return undefined;
    if (requireAllPermissions) {
      return permissions.find((permission) => !hasPermission(permission));
    }
    if (!hasAnyPermission(permissions)) {
      return permissions[0];
    }
    return undefined;
  }, [permissions, requireAllPermissions, hasPermission, hasAnyPermission]);

  // Check if user is authenticated
  if (!isAuthenticated) {
    return <Redirect to="/login" />;
  }

  // Show loading while user info is being fetched
  if (!userInfo) {
    return <LoadingSpiner />;
  }

  // Show loading while permissions are being loaded
  if (isLoading || !cachedPermissions) {
    return <LoadingSpiner />;
  }

  // Wait for company when a model capability is required (avoid flash-allow)
  if (needsCompanyGate && companyLoading) {
    return <LoadingSpiner />;
  }

  // If user doesn't have access, show fallback or redirect
  // (includes: required company model missing after fetch)
  if (!hasDmsAccess || !hasRequiredModelAccess || !hasAccess) {
    if (fallback) {
      return <>{fallback}</>;
    }
    return (
      <Redirect
        to={{
          pathname: redirectTo,
          state: { missingPermission },
        }}
      />
    );
  }

  // User has access, render the component
  return (
    <ErrorBoundary 
      FallbackComponent={ErrorFallback}
      onError={(error, errorInfo) => {
        console.error('PermissionRoute Error:', error, errorInfo);
      }}
    >
      <Route
        path={path}
        exact={exact}
        render={(props) => (
          <Suspense fallback={<LoadingSpiner />}>
            <Component {...props} {...rest} />
          </Suspense>
        )}
      />
    </ErrorBoundary>
  );
};

export default PermissionRoute; 